import { TimePickerSettings, BufferTimeSettings } from '../types';
import {
  isTimeInRange,
  isTimeExcluded,
  isOverlappingWithLunchTime,
  DEFAULT_BUFFER_TIME_SETTINGS,
} from './timePickerDefaults';

/**
 * FL-59: 時間バリデーション関連のユーティリティ
 */

export interface TimeValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * 単一時間の有効性をチェック
 * @param time - チェックする時間（HH:mm形式）
 * @param settings - 時間ピッカー設定
 * @returns バリデーション結果
 */
export const validateTime = (time: string, settings: TimePickerSettings): TimeValidationResult => {
  if (!time) {
    return { isValid: false, errorMessage: '時間を選択してください' };
  }

  // 時間形式チェック（HH:mm）
  const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timePattern.test(time)) {
    return { isValid: false, errorMessage: '正しい時間形式で入力してください（HH:mm）' };
  }

  // 除外時間チェック（範囲チェックより先に実行）
  if (isTimeExcluded(time, settings.excludedHours)) {
    return {
      isValid: false,
      errorMessage: '選択できない時間帯です',
    };
  }

  // 時間範囲チェック
  if (!isTimeInRange(time, settings.minTime, settings.maxTime)) {
    return {
      isValid: false,
      errorMessage: `時間は${settings.minTime}から${settings.maxTime}の間で選択してください`,
    };
  }

  return { isValid: true };
};

/**
 * 引き取り時間と搬入時間の関係をチェック
 * @param pickupTime - 引き取り時間（HH:mm形式）
 * @param deliveryTime - 搬入時間（HH:mm形式）
 * @param minimumDuration - 最小間隔（分）
 * @returns バリデーション結果
 */
export const validateTimeRelation = (
  pickupTime: string,
  deliveryTime: string,
  minimumDuration: number
): TimeValidationResult => {
  if (!pickupTime || !deliveryTime) {
    return { isValid: false, errorMessage: '引き取り時間と搬入時間の両方を入力してください' };
  }

  // 時間を分に変換（NaN対策）
  const [pickupHour, pickupMinute] = pickupTime.split(':').map(Number);
  const [deliveryHour, deliveryMinute] = deliveryTime.split(':').map(Number);

  // NaNチェック
  if (isNaN(pickupHour) || isNaN(pickupMinute) || isNaN(deliveryHour) || isNaN(deliveryMinute)) {
    return {
      isValid: false,
      errorMessage: '正しい時間形式で入力してください',
    };
  }

  const pickupMinutes = pickupHour * 60 + pickupMinute;
  const deliveryMinutes = deliveryHour * 60 + deliveryMinute;

  // 搬入時間が引き取り時間より前でないかチェック（同じ時間は許可）
  if (deliveryMinutes < pickupMinutes) {
    return {
      isValid: false,
      errorMessage: '搬入時間は引き取り時間より後に設定してください',
    };
  }

  // 最小間隔チェック
  const actualDuration = deliveryMinutes - pickupMinutes;
  if (actualDuration < minimumDuration) {
    const requiredHours = Math.floor(minimumDuration / 60);
    const requiredMinutes = minimumDuration % 60;
    const durationText =
      requiredHours > 0
        ? `${requiredHours}時間${requiredMinutes > 0 ? requiredMinutes + '分' : ''}`
        : `${requiredMinutes}分`;

    return {
      isValid: false,
      errorMessage: `引き取りから搬入まで最低${durationText}必要です`,
    };
  }

  return { isValid: true };
};

/**
 * 案件フォーム全体の時間バリデーション
 * @param formData - フォームデータ
 * @param pickupSettings - 引き取り時間設定
 * @param deliverySettings - 搬入時間設定
 * @returns バリデーション結果のマップ
 */
export const validateCaseFormTimes = (
  formData: {
    pickupTimeStart: string;
    pickupTimeEnd: string;
    deliveryTime: string;
  },
  pickupSettings: TimePickerSettings,
  deliverySettings: TimePickerSettings
): Record<string, TimeValidationResult> => {
  const results: Record<string, TimeValidationResult> = {};

  // 引き取り開始時間
  results.pickupTimeStart = validateTime(formData.pickupTimeStart, pickupSettings);

  // 引き取り終了時間
  results.pickupTimeEnd = validateTime(formData.pickupTimeEnd, pickupSettings);

  // 搬入時間
  results.deliveryTime = validateTime(formData.deliveryTime, deliverySettings);

  // 引き取り開始と終了の関係
  if (results.pickupTimeStart.isValid && results.pickupTimeEnd.isValid) {
    const relationResult = validateTimeRelation(
      formData.pickupTimeStart,
      formData.pickupTimeEnd,
      30 // 引き取り作業に最低30分
    );
    if (!relationResult.isValid) {
      results.pickupTimeEnd = relationResult;
    }
  }

  // 引き取り開始と搬入の関係
  if (results.pickupTimeStart.isValid && results.deliveryTime.isValid) {
    const relationResult = validateTimeRelation(
      formData.pickupTimeStart,
      formData.deliveryTime,
      deliverySettings.minimumDuration
    );
    if (!relationResult.isValid) {
      results.deliveryTime = relationResult;
    }
  }

  return results;
};

/**
 * バリデーション結果に1つでもエラーがあるかチェック
 * @param validationResults - バリデーション結果のマップ
 * @returns エラーがある場合true
 */
export const hasValidationErrors = (
  validationResults: Record<string, TimeValidationResult>
): boolean => {
  return Object.values(validationResults).some(result => !result.isValid);
};

/**
 * 最初のバリデーションエラーメッセージを取得
 * @param validationResults - バリデーション結果のマップ
 * @returns 最初のエラーメッセージ、エラーがない場合はundefined
 */
export const getFirstValidationError = (
  validationResults: Record<string, TimeValidationResult>
): string | undefined => {
  const firstError = Object.values(validationResults).find(result => !result.isValid);
  return firstError?.errorMessage;
};

/**
 * 時間文字列を分単位に変換
 * @param timeString - 時間文字列（HH:MM または HH:MM:SS 形式）
 * @returns 0時からの経過分数
 */
export const timeToMinutes = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * FL-81: バッファ時間を考慮した案件時間のバリデーション
 * @param formData - フォームデータ
 * @param pickupSettings - 引き取り時間設定
 * @param deliverySettings - 搬入時間設定
 * @param bufferSettings - バッファ時間設定
 * @returns バリデーション結果のマップ
 */
export const validateCaseFormTimesWithBuffer = (
  formData: {
    pickupTimeStart: string;
    pickupTimeEnd: string;
    deliveryTime: string;
  },
  pickupSettings: TimePickerSettings,
  deliverySettings: TimePickerSettings,
  bufferSettings: BufferTimeSettings = DEFAULT_BUFFER_TIME_SETTINGS
): Record<string, TimeValidationResult> => {
  // 基本的なバリデーションを実行
  const results = validateCaseFormTimes(formData, pickupSettings, deliverySettings);

  // 昼休み時間との重複チェック
  if (results.pickupTimeStart.isValid && results.deliveryTime.isValid) {
    if (
      isOverlappingWithLunchTime(
        formData.pickupTimeStart,
        formData.deliveryTime,
        bufferSettings.lunchTimeBuffer
      )
    ) {
      results.deliveryTime = {
        isValid: false,
        errorMessage: `昼休み時間（${bufferSettings.lunchTimeBuffer.startTime}-${bufferSettings.lunchTimeBuffer.endTime}）と重複しています。昼休み時間を避けて時間を設定してください。`,
      };
    }
  }

  return results;
};

/**
 * FL-81: バッファ時間設定のバリデーション
 * @param bufferSettings - バッファ時間設定
 * @returns バリデーション結果
 */
export const validateBufferTimeSettings = (
  bufferSettings: BufferTimeSettings
): TimeValidationResult => {
  // 案件間移動バッファの妥当性チェック
  if (bufferSettings.betweenCasesBuffer < 0 || bufferSettings.betweenCasesBuffer > 180) {
    return {
      isValid: false,
      errorMessage: '案件間移動バッファは0分から180分の範囲で設定してください',
    };
  }

  // 昼休みバッファの妥当性チェック
  if (
    bufferSettings.lunchTimeBuffer.bufferMinutes < 0 ||
    bufferSettings.lunchTimeBuffer.bufferMinutes > 240
  ) {
    return {
      isValid: false,
      errorMessage: '昼休みバッファは0分から240分の範囲で設定してください',
    };
  }

  // 昼休み時間の形式チェック
  const lunchTimePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (
    !lunchTimePattern.test(bufferSettings.lunchTimeBuffer.startTime) ||
    !lunchTimePattern.test(bufferSettings.lunchTimeBuffer.endTime)
  ) {
    return {
      isValid: false,
      errorMessage: '昼休み時間は正しい時間形式（HH:mm）で設定してください',
    };
  }

  // 準備時間バッファの妥当性チェック
  if (bufferSettings.dailyPreparationBuffer < 0 || bufferSettings.dailyPreparationBuffer > 60) {
    return {
      isValid: false,
      errorMessage: '準備時間バッファは0分から60分の範囲で設定してください',
    };
  }

  // 距離別バッファの妥当性チェック
  const { local, prefectural, crossPrefecture } = bufferSettings.longDistanceBuffer;
  if (
    local < 0 ||
    local > 120 ||
    prefectural < 0 ||
    prefectural > 120 ||
    crossPrefecture < 0 ||
    crossPrefecture > 180
  ) {
    return {
      isValid: false,
      errorMessage:
        '距離別バッファは適切な範囲で設定してください（市内・県内: 0-120分、県外: 0-180分）',
    };
  }

  return { isValid: true };
};
